/*
 * Los tres embudos, operables: universo → zonas calientes → cartera priorizada →
 * primer encargo, y el cierre D5. Es la parte del método que mueve la fase; antes
 * vivía solo en la vista anterior y por eso el descubrimiento no cambiaba por más
 * datos que se capturaran.
 *
 * Cada acción exige lo que el método exige y nada más: una zona nace de hallazgos
 * verificados, una oportunidad nace de una zona con anclaje verificado, la Matriz
 * se puntúa con el cliente delante, y las tres compuertas se validan con evidencia.
 * Lo que el dominio rechaza se muestra tal cual lo dice el servidor.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { ErrorApi, idNuevo, sesion as yo } from "../../api/cliente";
import {
  useAbrirEvaluacion, useAbrirPrimerEncargo, useConfirmarGobernanza, useCongelarRanking, useConsenso,
  useCrearOportunidad, useCrearZona, useDelta, useEstadoZona, useOportunidades, usePonerArtefacto,
  usePonerBaseline, usePonerIndicador, usePonerTerminos, usePrimerEncargo, useRegistrarProceso,
  useValidarCompuerta, useVotar, useZonas,
} from "../../api/hooks";
import {
  CRITERIOS, ESTADOS_ZONA, EQUIPO, nombreEquipo, type Hallazgo, type Oportunidad,
  type PasoProceso, type Persona, type Puntajes, type Sesion, type Transicion, type Vista, type Zona,
} from "../../api/tipos";
import { useAvisos } from "../../shell/Aviso";
import { Campo, Modal, Seg, grid2 } from "../../ui/Modal";
import { Abierto, CabeceraSeccion, Num, hoyISO } from "../../ui/primitivas";
import { PasoDeFase, type AccionRequisito } from "./PasoDeFase";

type Ctx = {
  casoId: string;
  cuentaId: string;
  vista: Vista;
  verificados: Hallazgo[];
  personas: Persona[];
  sesiones: Sesion[];
};

const CERO: Puntajes = { impacto: 3, tiempo: 3, factibilidad: 3, baseline: 3, respeto: 3 };

function mensaje(e: unknown): string {
  return e instanceof ErrorApi ? e.message : "No se pudo guardar.";
}

/** Selector de hallazgos verificados: lo que sostiene zonas, anclas y compuertas. */
function PickHallazgos({ hallazgos, elegidos, onChange, vacio }: { hallazgos: Hallazgo[]; elegidos: string[]; onChange: (v: string[]) => void; vacio: string }) {
  if (hallazgos.length === 0) return <Abierto style={{ padding: "8px 10px", fontSize: 12.5 }}>{vacio}</Abierto>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", border: "1px solid var(--border)", borderRadius: "var(--r-control)", padding: 8 }}>
      {hallazgos.map((h) => (
        <label key={h.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, lineHeight: 1.4, cursor: "pointer" }}>
          <input type="checkbox" checked={elegidos.includes(h.id)} style={{ width: "auto", marginTop: 3 }}
            onChange={() => onChange(elegidos.includes(h.id) ? elegidos.filter((x) => x !== h.id) : [...elegidos, h.id])} />
          <span><span className="tag11" style={{ color: "var(--faint)", marginRight: 6 }}>{h.tipo}</span>{h.contenido}</span>
        </label>
      ))}
    </div>
  );
}

export function Embudos({ ctx, transiciones, onSesion, onPersona, onHallazgo }: {
  ctx: Ctx;
  transiciones: Transicion[];
  onSesion: () => void;
  onPersona: () => void;
  onHallazgo: () => void;
}) {
  const { casoId, cuentaId, vista } = ctx;
  const { data: zonas } = useZonas(casoId);
  const { data: oportunidades } = useOportunidades(casoId);
  const { data: pe } = usePrimerEncargo(casoId);
  const [modal, setModal] = useState<null | { tipo: string; datos?: unknown }>(null);
  const cerrar = () => setModal(null);

  const zs = zonas ?? [];
  const activas = zs.filter((z) => z.estado !== "descartada");
  const ops = [...(oportunidades ?? [])].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const elegido = !!pe && pe.compuertas.every((g) => g.cumple === true);
  const conConsenso = ops.filter((o) => o.tiene_consenso);

  /**
   * El atajo de cada requisito abre justo el diálogo que lo resuelve. Cuando hay
   * varios candidatos (zonas sin proceso, fichas sin consenso) elige el primero
   * que de verdad falta: es lo que haría alguien que conoce el caso.
   */
  const accion = (a: AccionRequisito) => {
    const sinProceso = zs.find((z) => z.estado !== "descartada" && (!z.tiene_proceso_observado || !z.tiene_proceso_declarado || !z.delta_confirmado)) ?? activas[0];
    const sinConsenso = ops.find((o) => !o.tiene_consenso) ?? ops[0];
    const compuertaPendiente = pe?.compuertas.find((g) => g.cumple === null);
    switch (a) {
      case "sesion": return onSesion();
      case "persona": return onPersona();
      case "hallazgo": return onHallazgo();
      case "zona": return setModal({ tipo: "zona" });
      case "gobernanza": return setModal({ tipo: "gobernanza" });
      case "artefacto": return setModal({ tipo: "artefacto" });
      case "proceso": return sinProceso ? setModal({ tipo: "proceso", datos: sinProceso }) : setModal({ tipo: "zona" });
      case "ficha": return setModal({ tipo: "oportunidad" });
      case "matriz": return sinConsenso
        ? setModal({ tipo: sinConsenso.evaluacion_abierta ? (sinConsenso.votos.length ? "consenso" : "votar") : "abrirEval", datos: sinConsenso })
        : setModal({ tipo: "oportunidad" });
      case "congelar": return setModal({ tipo: "congelar" });
      case "primer-encargo": return ops[0] ? setModal({ tipo: "abrirPE", datos: ops[0] }) : setModal({ tipo: "oportunidad" });
      case "compuerta": return compuertaPendiente ? setModal({ tipo: "compuerta", datos: compuertaPendiente }) : undefined;
      case "baseline": return setModal({ tipo: "baseline" });
      case "indicador": return setModal({ tipo: "indicador" });
      case "terminos": return setModal({ tipo: "terminos" });
      case "hoja": return setModal({ tipo: "hoja" });
      default: return undefined;
    }
  };

  const paso = (n: number, titulo: string, ok: boolean, sub: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
      <Num n={n} hecho={ok} activo={!ok} /><span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{titulo}</span><span className="mono" style={{ color: ok ? "var(--text)" : "var(--faint)" }}>{sub}</span>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PasoDeFase casoId={casoId} cuentaId={cuentaId} transicion={transiciones[0]} onAccion={accion} />

      <section className="tarjeta">
        <h3 className="seccion-h" style={{ marginBottom: 6 }}>Los tres embudos</h3>
        {paso(1, "Universo → Zonas calientes", activas.length > 0, activas.length ? `${activas.length} zonas` : "3–6")}
        {paso(2, "Zonas → Cartera priorizada", conConsenso.length > 0, conConsenso.length ? `${conConsenso.length} con score` : "3–5")}
        {paso(3, "Cartera → Primer encargo", elegido, elegido ? "elegido" : "—")}
      </section>

      {/* ── Zonas calientes ── */}
      <section className="tarjeta">
        <CabeceraSeccion titulo="Zonas calientes" n={activas.length}
          derecha={<button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5 }} onClick={() => setModal({ tipo: "zona" })}>+ Marcar zona</button>} />
        {zs.length === 0 && <Abierto style={{ padding: "10px 12px", fontSize: 12.5 }}>Se marcan en La Plática y El Recorrido, sobre hallazgos verificados. Sin zonas no hay cartera.</Abierto>}
        {zs.map((z) => {
          const ui = ESTADOS_ZONA[z.estado];
          return (
            <div key={z.id} style={{ padding: "8px 0", borderTop: "1px solid var(--border)" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 13 }}>
                <span style={{ flex: 1, textDecoration: z.estado === "descartada" ? "line-through" : "none", color: z.estado === "descartada" ? "var(--muted)" : "var(--text)" }}>{z.nombre}</span>
                <span className="pastilla" style={{ background: ui.bg, color: ui.color, fontSize: 11 }}>{ui.label}</span>
                <span className="mono" style={{ color: "var(--faint)", fontSize: 11 }}>{z.hallazgos_evidencia.length} ev.</span>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap", fontSize: 11.5 }}>
                <span style={{ color: z.tiene_proceso_observado ? "var(--verified)" : "var(--faint)" }}>{z.tiene_proceso_observado ? "✓" : "○"} proceso observado</span>
                <span style={{ color: z.delta_confirmado ? "var(--verified)" : "var(--faint)" }}>{z.delta_confirmado ? "✓" : "○"} delta{z.delta_magnitud ? ` ${z.delta_magnitud}` : ""}</span>
                {z.estado !== "descartada" && (
                  <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <Link to={`/cuentas/${cuentaId}/playground?zona=${encodeURIComponent(z.id)}`} aria-label={`Explorar ideas para ${z.nombre}`} style={{ fontSize: 11.5, color: "var(--sky)", textDecoration: "none" }}>✦ Ideas</Link>
                    <button className="btn btn-fantasma" aria-label={`Proceso de ${z.nombre}`} style={{ padding: 0, fontSize: 11.5 }} onClick={() => setModal({ tipo: "proceso", datos: z })}>Proceso…</button>
                    {z.estado === "marcada" && <button className="btn btn-fantasma" aria-label={`Pasar ${z.nombre} a finalista`} style={{ padding: 0, fontSize: 11.5 }} onClick={() => setModal({ tipo: "finalista", datos: z })}>Finalista</button>}
                    <button className="btn btn-fantasma" aria-label={`Descartar ${z.nombre}`} style={{ padding: 0, fontSize: 11.5, color: "var(--risk)" }} onClick={() => setModal({ tipo: "descartar", datos: z })}>Descartar</button>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {/* ── Cartera · Matriz ── */}
      <section className="tarjeta">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, gap: 8 }}>
          <h3 className="seccion-h">Cartera · Matriz Nexlum</h3>
          <span style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
            <span className="mono" style={{ color: "var(--faint)", fontSize: 11 }}>30·30·20·15·5</span>
            <Link to={`/cuentas/${cuentaId}/playground`} style={{ fontSize: 12.5, color: "var(--sky)", textDecoration: "none" }}>✦ Playground</Link>
            <button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5 }} onClick={() => setModal({ tipo: "oportunidad" })}>+ Ficha</button>
          </span>
        </div>
        {ops.length === 0 && <Abierto style={{ padding: "10px 12px", fontSize: 12.5 }}>Las fichas de oportunidad salen de las zonas calientes, ancladas a hallazgos verificados.</Abierto>}
        {ops.map((o, i) => (
          <div key={o.id} style={{ padding: "8px 0", borderTop: "1px solid var(--border)" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 13 }}>
              <span className="mono" style={{ color: "var(--faint)" }}>#{o.rank ?? i + 1}</span>
              <span style={{ flex: 1, fontWeight: i === 0 && o.score !== null ? 500 : 400 }}>{o.titulo}</span>
              {o.score !== null ? <span className="mono" style={{ fontWeight: 500, color: o.score >= 3.5 ? "var(--verified)" : "var(--proposed)" }}>{o.score.toFixed(2)}</span> : <span style={{ fontSize: 11.5, color: "var(--open-text)" }}>sin puntuar</span>}
            </div>
            {o.riesgo_adopcion === "alto" && <span style={{ fontSize: 11.5, color: "var(--conflict)" }}>dispersión alta · riesgo de adopción</span>}
            <div style={{ display: "flex", gap: 10, marginTop: 4, fontSize: 11.5 }}>
              <span style={{ color: "var(--faint)" }}>{o.votos.length ? `${new Set(o.votos.map((v) => v.evaluador_id)).size} personas puntuaron` : "sin votos"}</span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                {!o.evaluacion_abierta && <button className="btn btn-fantasma" aria-label={`Abrir la Matriz para ${o.titulo}`} style={{ padding: 0, fontSize: 11.5 }} onClick={() => setModal({ tipo: "abrirEval", datos: o })}>Abrir Matriz</button>}
                {o.evaluacion_abierta && <button className="btn btn-fantasma" aria-label={`Puntuar ${o.titulo}`} style={{ padding: 0, fontSize: 11.5 }} onClick={() => setModal({ tipo: "votar", datos: o })}>Puntuar</button>}
                {o.evaluacion_abierta && !o.tiene_consenso && <button className="btn btn-fantasma" aria-label={`Acordar consenso de ${o.titulo}`} style={{ padding: 0, fontSize: 11.5 }} onClick={() => setModal({ tipo: "consenso", datos: o })}>Consenso</button>}
              </span>
            </div>
          </div>
        ))}
        {ops.length > 0 && <Congelar casoId={casoId} cuentaId={cuentaId} listas={conConsenso.length === ops.length} sinRank={ops.some((o) => o.rank === null)} />}
        <p style={{ fontSize: 11.5, color: "var(--faint)", marginTop: 8 }}>Umbral: #1 ≥ 3.5 y tres compuertas. Se corre en vivo en el Taller.</p>
      </section>

      {/* ── Primer encargo ── */}
      <section className="tarjeta">
        <CabeceraSeccion titulo="Primer encargo"
          derecha={!pe && ops.length > 0 ? <button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5 }} onClick={() => setModal({ tipo: "abrirPE", datos: ops[0] })}>Abrir sobre #1</button> : undefined} />
        {!pe && <Abierto style={{ padding: "10px 12px", fontSize: 12.5 }}>Se abre sobre la oportunidad #1 y no pasa sin sus tres compuertas. Un score alto que falla una compuerta no compensa.</Abierto>}
        {pe && (
          <>
            <p style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 6 }}>{ops.find((o) => o.id === pe.oportunidad_id)?.titulo} · {pe.estado}</p>
            {pe.compuertas.map((g) => (
              <div key={g.clave} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "5px 0", borderTop: "1px solid var(--border)", fontSize: 12.5 }}>
                <span style={{ color: g.cumple === true ? "var(--verified)" : g.cumple === false ? "var(--risk)" : "var(--open-text)" }}>{g.cumple === true ? "✓" : g.cumple === false ? "✕" : "○"}</span>
                <span style={{ flex: 1 }}>{g.descripcion}{g.justificacion && <span style={{ display: "block", color: "var(--faint)", fontSize: 11.5 }}>{g.justificacion}</span>}</span>
                {g.cumple === null && <button className="btn btn-fantasma" aria-label={`Validar compuerta: ${g.descripcion}`} style={{ padding: 0, fontSize: 11.5 }} onClick={() => setModal({ tipo: "compuerta", datos: g })}>Validar</button>}
              </div>
            ))}
          </>
        )}
      </section>

      {/* ── Cierre D5 ── */}
      <section className="tarjeta">
        <h3 className="seccion-h" style={{ marginBottom: 8 }}>El cierre · D5</h3>
        {vista.cierre.indicador ? (
          <div style={{ marginBottom: 10 }}>
            <span className="tag11" style={{ color: "var(--faint)", display: "block" }}>Indicador</span>
            <span className="subtitulo" style={{ fontSize: 14 }}>{vista.cierre.indicador.kpi}</span>
            {vista.cierre.baseline && <><span className="tag11" style={{ color: "var(--faint)", display: "block", marginTop: 6 }}>Baseline</span><span className="mono" style={{ fontSize: 12.5 }}>{vista.cierre.baseline.numero} {vista.cierre.baseline.unidad} · {vista.cierre.baseline.fuente.sistema}</span></>}
          </div>
        ) : <Abierto style={{ padding: "10px 12px", fontSize: 12.5, marginBottom: 10 }}>Sin indicador. Se acuerda en el Taller de Enfoque: un número, su fuente exacta y quién lo valida.</Abierto>}
        {([
          ["Baseline firmado", !!vista.cierre.baseline, "baseline"],
          ["Indicador por escrito", !!vista.cierre.indicador, "indicador"],
          ["Términos y fee-at-risk", !!vista.cierre.terminos, "terminos"],
        ] as [string, boolean, string][]).map(([t, ok, tipo]) => (
          <div key={t} style={{ display: "flex", gap: 8, fontSize: 12.5, padding: "4px 0", alignItems: "baseline" }}>
            <span style={{ color: ok ? "var(--verified)" : "var(--border-strong)" }}>{ok ? "✓" : "○"}</span>
            <span style={{ flex: 1, color: ok ? "var(--text)" : "var(--faint)" }}>{t}</span>
            {/* Se puede corregir después: el cierre se negocia, y un dato que no se
                puede arreglar obliga a salirse de la herramienta para arreglarlo. */}
            <button className="btn btn-fantasma" aria-label={`${ok ? "Corregir" : "Capturar"} ${t}`} style={{ padding: 0, fontSize: 11.5 }} onClick={() => setModal({ tipo })}>{ok ? "Corregir…" : "Capturar…"}</button>
          </div>
        ))}
        <HojaDeUnNumero ctx={ctx} listo={!!vista.cierre.baseline && !!vista.cierre.indicador && !!vista.cierre.terminos} />
      </section>

      {modal?.tipo === "zona" && <FormZona ctx={ctx} cerrar={cerrar} />}
      {modal?.tipo === "finalista" && <CambiarZona ctx={ctx} zona={modal.datos as Zona} estado="finalista" cerrar={cerrar} />}
      {modal?.tipo === "descartar" && <CambiarZona ctx={ctx} zona={modal.datos as Zona} estado="descartada" cerrar={cerrar} />}
      {modal?.tipo === "proceso" && <FormProceso ctx={ctx} zonaId={(modal.datos as Zona).id} cerrar={cerrar} />}
      {modal?.tipo === "oportunidad" && <FormOportunidad ctx={ctx} zonas={activas} cerrar={cerrar} />}
      {modal?.tipo === "abrirEval" && <AbrirEval ctx={ctx} o={modal.datos as Oportunidad} cerrar={cerrar} />}
      {modal?.tipo === "votar" && <FormVoto ctx={ctx} o={modal.datos as Oportunidad} cerrar={cerrar} />}
      {modal?.tipo === "consenso" && <FormConsenso ctx={ctx} o={modal.datos as Oportunidad} cerrar={cerrar} />}
      {modal?.tipo === "abrirPE" && <AbrirPE ctx={ctx} o={modal.datos as Oportunidad} cerrar={cerrar} />}
      {modal?.tipo === "compuerta" && <FormCompuerta ctx={ctx} compuerta={modal.datos as { clave: string; descripcion: string }} cerrar={cerrar} />}
      {modal?.tipo === "baseline" && <FormBaseline ctx={ctx} cerrar={cerrar} />}
      {modal?.tipo === "indicador" && <FormIndicador ctx={ctx} cerrar={cerrar} />}
      {modal?.tipo === "terminos" && <FormTerminos ctx={ctx} cerrar={cerrar} />}
      {modal?.tipo === "gobernanza" && <ElegirGobernanza ctx={ctx} cerrar={cerrar} />}
      {modal?.tipo === "artefacto" && <BotonArtefacto ctx={ctx} auto onCerrar={cerrar} />}
      {modal?.tipo === "hoja" && <HojaDeUnNumero ctx={ctx} listo auto onCerrar={cerrar} />}
      {modal?.tipo === "congelar" && <AvisoCongelar cerrar={cerrar} />}
    </div>
  );
}

// ─── Zonas ──────────────────────────────────────────────────────────────────

function FormZona({ ctx, cerrar }: { ctx: Ctx; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const crear = useCrearZona(ctx.casoId, ctx.cuentaId);
  const [nombre, setNombre] = useState("");
  const [ev, setEv] = useState<string[]>([]);
  const [error, setError] = useState("");
  const guardar = async () => {
    if (!nombre.trim() || ev.length === 0) { setError("Una zona necesita nombre y al menos un hallazgo verificado que la sostenga."); return; }
    try {
      await crear.mutateAsync({ id: idNuevo("z"), nombre: nombre.trim(), hallazgos_evidencia: ev });
      cerrar(); avisar(`Zona «${nombre.trim()}» marcada`);
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Marcar zona caliente" sub="Una zona no existe sin evidencia verificada detrás." onCerrar={cerrar} ancho={560}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={crear.isPending} onClick={guardar}>Marcar zona</button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Campo label="Nombre de la zona" error={error}><input autoFocus value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Reporte diario de producción" /></Campo>
        <Campo label="Evidencia" pista="Hallazgos verificados que sostienen esta zona">
          <PickHallazgos hallazgos={ctx.verificados} elegidos={ev} onChange={setEv} vacio="No hay hallazgos verificados todavía. Verifica primero la cola: sin evidencia no hay zona." />
        </Campo>
      </div>
    </Modal>
  );
}

function CambiarZona({ ctx, zona, estado, cerrar }: { ctx: Ctx; zona: Zona; estado: "finalista" | "descartada"; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const cambiar = useEstadoZona(ctx.casoId, ctx.cuentaId);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState("");
  const descartar = estado === "descartada";
  const guardar = async () => {
    try {
      await cambiar.mutateAsync({ zonaId: zona.id, estado, motivo_descarte: descartar ? motivo.trim() : null });
      cerrar(); avisar(descartar ? `«${zona.nombre}» descartada` : `«${zona.nombre}» pasa a finalista`);
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo={descartar ? "Descartar zona" : "Pasar a finalista"} sub={zona.nombre} onCerrar={cerrar} ancho={460}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className={descartar ? "btn btn-peligro" : "btn btn-primario"} aria-label={descartar ? "Confirmar descarte" : "Confirmar finalista"} disabled={cambiar.isPending || (descartar && !motivo.trim())} onClick={guardar}>{descartar ? "Descartar" : "Finalista"}</button></>}>
      {descartar
        ? <Campo label="Por qué se descarta" error={error}><input autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Queda fuera del alcance del primer encargo" /></Campo>
        : <p style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--muted)" }}>Las finalistas son las que se caminan en El Recorrido y las que la Matriz evalúa. Cada una necesitará proceso observado y un hallazgo de campo.{error && <span style={{ display: "block", color: "var(--risk)", marginTop: 8 }}>{error}</span>}</p>}
    </Modal>
  );
}

function FormProceso({ ctx, zonaId, cerrar }: { ctx: Ctx; zonaId: string; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const registrar = useRegistrarProceso(ctx.casoId, ctx.cuentaId);
  const delta = useDelta(ctx.casoId, ctx.cuentaId);
  // La zona se lee viva, no de una copia: guardar un proceso habilita el delta en
  // el mismo diálogo, y con una copia los botones se quedaban apagados.
  const { data: zonas } = useZonas(ctx.casoId);
  const zona = zonas?.find((z) => z.id === zonaId);
  const [origen, setOrigen] = useState<"observado" | "declarado">("observado");
  const [sesionId, setSesionId] = useState(ctx.sesiones[ctx.sesiones.length - 1]?.id ?? "");
  const [pasos, setPasos] = useState<PasoProceso[]>([{ nombre: "", duracion_seg: null, espera_seg: null, es_reproceso: false }]);
  const [error, setError] = useState("");
  const set = (i: number, k: keyof PasoProceso, v: unknown) => setPasos((p) => p.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const guardar = async () => {
    const limpios = pasos.filter((p) => p.nombre.trim() !== "");
    if (limpios.length < 3) { setError("El proceso observado necesita al menos tres pasos: es lo que hace comparable el Happy Path."); return; }
    if (!sesionId) { setError("¿En qué sesión se observó? Registra la sesión primero."); return; }
    try {
      await registrar.mutateAsync({ zonaId, origen, sesion_id: sesionId, pasos: limpios });
      avisar(`Proceso ${origen} de «${zona?.nombre ?? "la zona"}» registrado`);
      // Se queda abierto: lo normal es capturar el declarado y el observado seguidos,
      // y en cuanto están los dos se puede calcular el delta aquí mismo.
      setPasos([{ nombre: "", duracion_seg: null, espera_seg: null, es_reproceso: false }]);
      setOrigen(origen === "observado" ? "declarado" : "observado");
      setError("");
    } catch (e) { setError(mensaje(e)); }
  };
  const correrDelta = async (confirmar: boolean) => {
    try {
      await delta.mutateAsync({ zonaId, confirmar, confirmador_id: yo.quien() });
      avisar(confirmar ? "Delta confirmado" : "Delta calculado");
      if (confirmar) cerrar();
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Proceso de la zona" sub={`${zona?.nombre ?? ""} · el contraste entre lo que se dice y lo que se ve`} onCerrar={cerrar} ancho={640}
      pie={<>
        <span style={{ marginRight: "auto", display: "flex", gap: 8 }}>
          <button className="btn btn-secundario" disabled={!zona?.tiene_proceso_declarado || !zona?.tiene_proceso_observado || delta.isPending} onClick={() => correrDelta(false)}>Calcular delta</button>
          <button className="btn btn-secundario" disabled={!zona?.delta_magnitud || delta.isPending} onClick={() => correrDelta(true)}>Confirmar delta</button>
        </span>
        <button className="btn btn-secundario" onClick={cerrar}>Cerrar</button>
        <button className="btn btn-primario" disabled={registrar.isPending} onClick={guardar}>Guardar proceso</button>
      </>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={grid2}>
          <Campo label="Origen" pista={origen === "observado" ? `Lo que se vio en El Recorrido${zona?.tiene_proceso_observado ? " · ya registrado" : ""}` : `Lo que se contó en La Plática${zona?.tiene_proceso_declarado ? " · ya registrado" : ""}`}>
            <Seg value={origen} onChange={setOrigen} opciones={[["observado", "Observado"], ["declarado", "Declarado"]]} />
          </Campo>
          <Campo label="Sesión"><select value={sesionId} onChange={(e) => setSesionId(e.target.value)}>{ctx.sesiones.map((s) => <option key={s.id} value={s.id}>{s.tipo} · {s.fecha.slice(0, 10)}</option>)}</select></Campo>
        </div>
        <Campo label="Pasos" error={error} pista="Nombre, y si lo mediste, cuánto dura y cuánto espera">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {pasos.map((p, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 90px 90px auto", gap: 6, alignItems: "center" }}>
                <input value={p.nombre} onChange={(e) => set(i, "nombre", e.target.value)} placeholder={`Paso ${i + 1}`} />
                <input type="number" value={p.duracion_seg ?? ""} onChange={(e) => set(i, "duracion_seg", e.target.value === "" ? null : Number(e.target.value))} placeholder="dura s" />
                <input type="number" value={p.espera_seg ?? ""} onChange={(e) => set(i, "espera_seg", e.target.value === "" ? null : Number(e.target.value))} placeholder="espera s" />
                <label style={{ fontSize: 11.5, color: "var(--muted)", display: "flex", gap: 4, alignItems: "center", whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={!!p.es_reproceso} onChange={(e) => set(i, "es_reproceso", e.target.checked)} style={{ width: "auto" }} />retrabajo
                </label>
              </div>
            ))}
            <button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5, alignSelf: "flex-start" }} onClick={() => setPasos((p) => [...p, { nombre: "", duracion_seg: null, espera_seg: null, es_reproceso: false }])}>+ Otro paso</button>
          </div>
        </Campo>
      </div>
    </Modal>
  );
}

// ─── Cartera ────────────────────────────────────────────────────────────────

export type FichaBorrador = { titulo: string; linea: string; zona: string; anclas: string[] };

/**
 * La ficha de siempre. Desde la exploración llega con `inicial` (lo que propuso la
 * IA, editable) y con su propio `guardar`, que convierte la idea en vez de crear una
 * oportunidad suelta; la zona entonces queda fija, porque es la de la idea.
 */
export function FormOportunidad({ ctx, zonas, cerrar, inicial, guardar: guardarFuera, sub, zonaFija }: {
  ctx: Pick<Ctx, "casoId" | "cuentaId" | "verificados">;
  zonas: Zona[];
  cerrar: () => void;
  inicial?: FichaBorrador;
  guardar?: (v: FichaBorrador) => Promise<unknown>;
  sub?: string;
  zonaFija?: boolean;
}) {
  const { avisar } = useAvisos();
  const crear = useCrearOportunidad(ctx.casoId, ctx.cuentaId);
  const [d, setD] = useState({ titulo: inicial?.titulo ?? "", linea: inicial?.linea ?? "", zona: inicial?.zona ?? zonas[0]?.id ?? "" });
  const [anclas, setAnclas] = useState<string[]>(inicial?.anclas ?? []);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const guardar = async () => {
    if (!d.titulo.trim() || !d.zona || anclas.length === 0) { setError("Título, zona y al menos un hallazgo verificado de anclaje."); return; }
    const v = { titulo: d.titulo.trim(), linea: d.linea.trim() || d.titulo.trim(), zona: d.zona, anclas };
    setEnviando(true);
    try {
      if (guardarFuera) await guardarFuera(v);
      else await crear.mutateAsync({ id: idNuevo("o"), titulo: v.titulo, descripcion_una_linea: v.linea, zona_caliente_id: v.zona, hallazgos_ancla: v.anclas });
      cerrar(); avisar(`Ficha «${v.titulo}» agregada a la cartera`);
    } catch (e) { setError(mensaje(e)); } finally { setEnviando(false); }
  };
  return (
    <Modal titulo="Ficha de oportunidad" sub={sub ?? "Sale de una zona caliente y se ancla a hallazgos verificados."} onCerrar={cerrar} ancho={560}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={enviando} onClick={guardar}>Agregar a la cartera</button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {zonas.length === 0 && <Abierto style={{ padding: "8px 10px", fontSize: 12.5 }}>Primero marca una zona caliente.</Abierto>}
        <Campo label="Título" error={error}><input autoFocus value={d.titulo} onChange={(e) => setD((x) => ({ ...x, titulo: e.target.value }))} placeholder="Automatizar el reporte diario de producción" /></Campo>
        <Campo label="En una línea"><input value={d.linea} onChange={(e) => setD((x) => ({ ...x, linea: e.target.value }))} placeholder="Qué cambia, en lenguaje llano" /></Campo>
        <Campo label="Zona"><select value={d.zona} disabled={zonaFija} onChange={(e) => setD((x) => ({ ...x, zona: e.target.value }))}>{zonas.map((z) => <option key={z.id} value={z.id}>{z.nombre}</option>)}</select></Campo>
        <Campo label="Anclaje"><PickHallazgos hallazgos={ctx.verificados} elegidos={anclas} onChange={setAnclas} vacio="Sin hallazgos verificados no hay anclaje posible." /></Campo>
      </div>
    </Modal>
  );
}

function AbrirEval({ ctx, o, cerrar }: { ctx: Ctx; o: Oportunidad; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const abrir = useAbrirEvaluacion(ctx.casoId, ctx.cuentaId);
  const talleres = ctx.sesiones.filter((s) => s.tipo === "S4_taller");
  const [sesionId, setSesionId] = useState(talleres[talleres.length - 1]?.id ?? "");
  const [error, setError] = useState("");
  const guardar = async () => {
    try {
      await abrir.mutateAsync({ oportunidadId: o.id, sesion_id: sesionId, evaluacion_id: idNuevo("ev") });
      cerrar(); avisar(`Matriz abierta para «${o.titulo}»`);
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Abrir la Matriz" sub={`${o.titulo} · se puntúa en el Taller, con el cliente delante`} onCerrar={cerrar} ancho={460}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={!sesionId || abrir.isPending} onClick={guardar}>Abrir</button></>}>
      {talleres.length === 0
        ? <Abierto style={{ padding: "10px 12px", fontSize: 13 }}>No hay ningún Taller de Enfoque registrado. La Matriz se corre ahí: registra la sesión S4 primero.</Abierto>
        : <Campo label="Taller" error={error}><select autoFocus value={sesionId} onChange={(e) => setSesionId(e.target.value)}>{talleres.map((s) => <option key={s.id} value={s.id}>Taller · {s.fecha.slice(0, 10)}</option>)}</select></Campo>}
    </Modal>
  );
}

function Puntuador({ valores, onChange }: { valores: Puntajes; onChange: (v: Puntajes) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {CRITERIOS.map((c) => (
        <div key={c.id} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 13 }}>{c.label} <span className="mono" style={{ color: "var(--faint)", fontSize: 11 }}>{c.peso}%</span></span>
          <div className="seg">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" className={valores[c.id] === n ? "activo" : ""} onClick={() => onChange({ ...valores, [c.id]: n })}>{n}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** La oportunidad, siempre la de ahora: votar cambia quién falta y si ya hay consenso. */
function useOportunidadViva(casoId: string, id: string, inicial: Oportunidad): Oportunidad {
  const { data } = useOportunidades(casoId);
  return data?.find((x) => x.id === id) ?? inicial;
}

function FormVoto({ ctx, o: inicial, cerrar }: { ctx: Ctx; o: Oportunidad; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const o = useOportunidadViva(ctx.casoId, inicial.id, inicial);
  const votar = useVotar(ctx.casoId, ctx.cuentaId);
  // Las personas del expediente ya incluyen a quien facilita y a quien comercializa.
  const gente = ctx.personas.length ? ctx.personas.map((p) => ({ id: p.id, nombre: p.nombre })) : EQUIPO;
  const yaVotaron = new Set(o.votos.map((v) => v.evaluador_id));
  const quienEs = (id: string) => ctx.personas.find((p) => p.id === id);
  const cobertura: [string, boolean][] = [
    ["dueño", [...yaVotaron].some((id) => quienEs(id)?.es_economic_buyer)],
    ["alguien más del cliente", [...yaVotaron].some((id) => quienEs(id)?.organizacion === "cliente" && !quienEs(id)?.es_economic_buyer)],
    ["alguien de Nexlum", [...yaVotaron].some((id) => quienEs(id)?.organizacion === "nexlum")],
  ];
  const [quien, setQuien] = useState(gente.find((g) => !yaVotaron.has(g.id))?.id ?? gente[0]?.id ?? "");
  const [v, setV] = useState<Puntajes>(CERO);
  const [error, setError] = useState("");
  const [n, setN] = useState(0);
  const guardar = async () => {
    try {
      await votar.mutateAsync({ oportunidadId: o.id, evaluador_id: quien, ...v });
      setN((x) => x + 1);
      avisar(`Puntuación de ${gente.find((g) => g.id === quien)?.nombre ?? quien} registrada`);
      const siguiente = gente.find((g) => g.id !== quien && !yaVotaron.has(g.id));
      if (siguiente) { setQuien(siguiente.id); setV(CERO); }
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Puntuar en la Matriz" sub={`${o.titulo}${n ? ` · ${n} puntuaciones en esta tanda` : ""}`} onCerrar={cerrar} ancho={520}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Terminar</button><button className="btn btn-primario" disabled={votar.isPending} onClick={guardar}>Guardar y siguiente</button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Campo label="Quién puntúa" error={error}>
          <select value={quien} onChange={(e) => setQuien(e.target.value)}>
            {gente.map((g) => <option key={g.id} value={g.id}>{g.nombre}{yaVotaron.has(g.id) ? " · ya puntuó" : ""}</option>)}
          </select>
        </Campo>
        {/* Quién falta, dicho aquí: la Matriz no cuenta para avanzar si no la
            puntuaron el dueño, alguien más del cliente y alguien de Nexlum. */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12 }}>
          {cobertura.map(([label, ok]) => (
            <span key={label} style={{ color: ok ? "var(--verified)" : "var(--open-text)" }}>{ok ? "✓" : "○"} {label}</span>
          ))}
        </div>
        <Puntuador valores={v} onChange={setV} />
      </div>
    </Modal>
  );
}

function FormConsenso({ ctx, o: inicial, cerrar }: { ctx: Ctx; o: Oportunidad; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const o = useOportunidadViva(ctx.casoId, inicial.id, inicial);
  const consenso = useConsenso(ctx.casoId, ctx.cuentaId);
  const participantes = [...new Set(o.votos.map((v) => v.evaluador_id))];
  const [v, setV] = useState<Puntajes>(CERO);
  const [error, setError] = useState("");
  const guardar = async () => {
    try {
      await consenso.mutateAsync({ oportunidadId: o.id, participantes, ...v });
      cerrar(); avisar(`Consenso acordado para «${o.titulo}»`);
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Acordar el consenso" sub={`${o.titulo} · el consenso es el único número que decide; el promedio de votos solo mide riesgo de adopción`} onCerrar={cerrar} ancho={520}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={participantes.length === 0 || consenso.isPending} onClick={guardar}>Acordar</button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {participantes.length === 0 && <Abierto style={{ padding: "8px 10px", fontSize: 12.5 }}>Nadie ha puntuado todavía. El consenso se acuerda después de que cada quien puso su número.</Abierto>}
        <Puntuador valores={v} onChange={setV} />
        {error && <span style={{ fontSize: 12.5, color: "var(--risk)" }}>{error}</span>}
        <span style={{ fontSize: 12, color: "var(--faint)" }}>Participantes: {participantes.map((p) => ctx.personas.find((x) => x.id === p)?.nombre ?? nombreEquipo(p)).join(", ") || "—"}</span>
      </div>
    </Modal>
  );
}

function Congelar({ casoId, cuentaId, listas, sinRank }: { casoId: string; cuentaId: string; listas: boolean; sinRank: boolean }) {
  const { avisar } = useAvisos();
  const congelar = useCongelarRanking(casoId, cuentaId);
  const [error, setError] = useState("");
  if (!sinRank) return <p style={{ fontSize: 11.5, color: "var(--verified)", marginTop: 8 }}>Ranking congelado.</p>;
  return (
    <div style={{ marginTop: 10 }}>
      <button className="btn btn-secundario" disabled={!listas || congelar.isPending}
        onClick={async () => { try { await congelar.mutateAsync(undefined); avisar("Ranking congelado"); } catch (e) { setError(mensaje(e)); } }}>
        Congelar ranking
      </button>
      {!listas && <span style={{ fontSize: 11.5, color: "var(--faint)", marginLeft: 8 }}>falta consenso en alguna ficha</span>}
      {error && <span style={{ fontSize: 11.5, color: "var(--risk)", marginLeft: 8 }}>{error}</span>}
    </div>
  );
}

// ─── Primer encargo ─────────────────────────────────────────────────────────

function AbrirPE({ ctx, o: inicial, cerrar }: { ctx: Ctx; o: Oportunidad; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const o = useOportunidadViva(ctx.casoId, inicial.id, inicial);
  const abrir = useAbrirPrimerEncargo(ctx.casoId, ctx.cuentaId);
  const [error, setError] = useState("");
  return (
    <Modal titulo="Abrir el primer encargo" sub={o.titulo} onCerrar={cerrar} ancho={460}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={abrir.isPending}
        onClick={async () => { try { await abrir.mutateAsync({ oportunidad_id: o.id, id: idNuevo("pe") }); cerrar(); avisar("Primer encargo abierto · faltan las tres compuertas"); } catch (e) { setError(mensaje(e)); } }}>Abrir</button></>}>
      <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>Score <span className="mono">{o.score?.toFixed(2) ?? "—"}</span>. A partir de aquí hay que validar las tres compuertas con evidencia; sin las tres, el caso no pasa a D5.</p>
      {error && <p style={{ fontSize: 12.5, color: "var(--risk)", marginTop: 8 }}>{error}</p>}
    </Modal>
  );
}

function FormCompuerta({ ctx, compuerta, cerrar }: { ctx: Ctx; compuerta: { clave: string; descripcion: string }; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const validar = useValidarCompuerta(ctx.casoId, ctx.cuentaId);
  const [cumple, setCumple] = useState(true);
  const [justificacion, setJustificacion] = useState("");
  const [evidencia, setEvidencia] = useState<string[]>([]);
  const [error, setError] = useState("");
  // Cada compuerta mira un tipo de evidencia distinto, y el dominio es estricto: la
  // de baseline solo acepta una métrica MARCADA como candidata a baseline, y la de
  // respeto una ansiedad. Filtrar aquí evita validar con algo que luego el paso de
  // fase rechaza sin decir por qué.
  const esCandidata = (h: Hallazgo) => h.tipo === "metrica" && h.atributos?.es_candidato_baseline === true;
  const sugeridos = ctx.verificados.filter((h) =>
    compuerta.clave === "g2_baseline_hoy" ? esCandidata(h)
    : compuerta.clave === "g3_respeta_lo_que_funciona" ? h.tipo === "ansiedad"
    : true);
  const vacio = compuerta.clave === "g2_baseline_hoy"
    ? "Ninguna métrica verificada está marcada como candidata a baseline. Vuelve a proponer la métrica con esa casilla: sin ella, esta compuerta no puede cumplirse."
    : compuerta.clave === "g3_respeta_lo_que_funciona"
      ? "No hay ninguna ansiedad verificada. Respetar lo que funciona se demuestra con lo que dijo la gente, no se declara."
      : "No hay hallazgos verificados todavía.";
  const guardar = async () => {
    if (!justificacion.trim() || evidencia.length === 0) { setError("Hace falta evidencia verificada y una justificación por escrito."); return; }
    try {
      await validar.mutateAsync({ clave: compuerta.clave, cumple, evidencia_hallazgo_ids: evidencia, justificacion: justificacion.trim(), validado_por: yo.quien() });
      cerrar(); avisar(cumple ? "Compuerta validada" : "Compuerta marcada como no cumplida");
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Validar compuerta" sub={compuerta.descripcion} onCerrar={cerrar} ancho={560}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={validar.isPending} onClick={guardar}>Guardar</button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Campo label="¿Se cumple?"><Seg value={cumple ? "si" : "no"} onChange={(v) => setCumple(v === "si")} opciones={[["si", "Sí"], ["no", "No"]]} /></Campo>
        <Campo label="Evidencia" error={error} pista={compuerta.clave === "g2_baseline_hoy" ? "Solo métricas candidatas a baseline: se mide hoy o no se mide" : compuerta.clave === "g3_respeta_lo_que_funciona" ? "Ansiedades escuchadas: respetar se demuestra, no se declara" : "Hallazgos verificados que la sostienen"}>
          <PickHallazgos hallazgos={sugeridos} elegidos={evidencia} onChange={setEvidencia} vacio={vacio} />
        </Campo>
        <Campo label="Justificación"><textarea value={justificacion} onChange={(e) => setJustificacion(e.target.value)} placeholder="Por qué esta compuerta se cumple, en una línea." /></Campo>
      </div>
    </Modal>
  );
}

// ─── Cierre D5 ──────────────────────────────────────────────────────────────

function FormBaseline({ ctx, cerrar }: { ctx: Ctx; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const poner = usePonerBaseline(ctx.casoId, ctx.cuentaId);
  const metricas = ctx.verificados.filter((h) => h.tipo === "metrica");
  const [d, setD] = useState({ numero: "", unidad: "", sistema: "", reporte: "", filtros: "", inicio: "", fin: hoyISO(), validado_por: "", medicion_lista: true, hallazgo: metricas[0]?.id ?? "", captura: "" });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const guardar = async () => {
    // Los filtros exactos no son opcionales: son lo que hace reproducible el número,
    // y la causa #1 de disputa es no haberlos acordado por escrito.
    if (!d.numero || !d.unidad.trim() || !d.sistema.trim() || !d.reporte.trim() || !d.filtros.trim() || !d.inicio) {
      setError("El Exhibit 1 necesita número, unidad, sistema, reporte, filtros exactos y la ventana histórica.");
      return;
    }
    try {
      await poner.mutateAsync({
        id: idNuevo("bl"), numero: Number(d.numero), unidad: d.unidad.trim(), sistema: d.sistema.trim(), reporte: d.reporte.trim(),
        filtros: d.filtros.trim(), ventana_inicio: d.inicio, ventana_fin: d.fin, validado_por: d.validado_por || null,
        medicion_lista: d.medicion_lista, fecha_medicion_lista: d.medicion_lista ? hoyISO() : null,
        hallazgo_metrica_id: d.hallazgo || null, captura_pantalla_uri: d.captura.trim() || null,
      });
      cerrar(); avisar("Baseline capturado · Exhibit 1");
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Baseline · Exhibit 1" sub="El número de hoy, con su fuente exacta. Es contra esto que se mide la garantía." onCerrar={cerrar} ancho={620}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={poner.isPending} onClick={guardar}>Guardar baseline</button></>}>
      <div style={grid2}>
        <Campo label="Número" error={error}><input autoFocus type="number" step="any" value={d.numero} onChange={(e) => u("numero")(e.target.value)} /></Campo>
        <Campo label="Unidad"><input value={d.unidad} onChange={(e) => u("unidad")(e.target.value)} placeholder="horas por semana" /></Campo>
        <Campo label="Sistema"><input value={d.sistema} onChange={(e) => u("sistema")(e.target.value)} placeholder="ERP Aspel SAE" /></Campo>
        <Campo label="Reporte"><input value={d.reporte} onChange={(e) => u("reporte")(e.target.value)} placeholder="Reporte semanal de conciliación" /></Campo>
        <Campo label="Filtros exactos" span pista="Con qué filtros se saca ese número: es lo que lo hace reproducible"><input value={d.filtros} onChange={(e) => u("filtros")(e.target.value)} placeholder="Sucursal, turno, semanas exactas" /></Campo>
        <Campo label="Ventana histórica · inicio"><input type="date" value={d.inicio} onChange={(e) => u("inicio")(e.target.value)} /></Campo>
        <Campo label="Ventana histórica · fin"><input type="date" value={d.fin} onChange={(e) => u("fin")(e.target.value)} /></Campo>
        <Campo label="Métrica que lo respalda" span><select value={d.hallazgo} onChange={(e) => u("hallazgo")(e.target.value)}><option value="">— ninguna —</option>{metricas.map((h) => <option key={h.id} value={h.id}>{h.contenido.slice(0, 70)}</option>)}</select></Campo>
        <Campo label="Quién lo valida (cliente)"><select value={d.validado_por} onChange={(e) => u("validado_por")(e.target.value)}><option value="">— por confirmar —</option>{ctx.personas.filter((p) => p.organizacion === "cliente").map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></Campo>
        <Campo label="Captura de la fuente" pista="ruta o URL del pantallazo"><input value={d.captura} onChange={(e) => u("captura")(e.target.value)} placeholder="evidencia/baseline.png" /></Campo>
        <Campo label="¿Se puede medir ya?" span><Seg value={d.medicion_lista ? "si" : "no"} onChange={(v) => u("medicion_lista")(v === "si")} opciones={[["si", "Sí, medible hoy"], ["no", "Todavía no"]]} /></Campo>
      </div>
    </Modal>
  );
}

function FormIndicador({ ctx, cerrar }: { ctx: Ctx; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const poner = usePonerIndicador(ctx.casoId, ctx.cuentaId);
  const [d, setD] = useState({ kpi: "", fuente: "", metodo: "", inicio: hoyISO(), fin: "", tipo: "cerrada", acordado: [] as string[] });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const gente = [...ctx.personas.map((p) => ({ id: p.id, nombre: p.nombre })), ...EQUIPO];
  const guardar = async () => {
    if (!d.kpi.trim() || !d.fuente.trim() || !d.metodo.trim() || !d.fin) { setError("El indicador necesita KPI, fuente, método de verificación y ventana."); return; }
    try {
      await poner.mutateAsync({ definicion_kpi: d.kpi.trim(), fuente_datos: d.fuente.trim(), metodo_verificacion: d.metodo.trim(), ventana_inicio: d.inicio, ventana_fin: d.fin, ventana_tipo: d.tipo, acordado_por: d.acordado });
      cerrar(); avisar("Indicador acordado");
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Indicador" sub="Un número, su fuente exacta, cómo se verifica y quién lo acordó." onCerrar={cerrar} ancho={620}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={poner.isPending} onClick={guardar}>Guardar indicador</button></>}>
      <div style={grid2}>
        <Campo label="KPI" span error={error}><input autoFocus value={d.kpi} onChange={(e) => u("kpi")(e.target.value)} placeholder="Horas semanales dedicadas a conciliar facturas" /></Campo>
        <Campo label="Fuente del dato" span><input value={d.fuente} onChange={(e) => u("fuente")(e.target.value)} placeholder="Reporte semanal del ERP, exportado los lunes" /></Campo>
        <Campo label="Método de verificación" span><textarea value={d.metodo} onChange={(e) => u("metodo")(e.target.value)} placeholder="Cómo se compara contra el baseline y quién lo valida." /></Campo>
        <Campo label="Ventana · inicio"><input type="date" value={d.inicio} onChange={(e) => u("inicio")(e.target.value)} /></Campo>
        <Campo label="Ventana · fin"><input type="date" value={d.fin} onChange={(e) => u("fin")(e.target.value)} /></Campo>
        <Campo label="Acordado por" span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {gente.map((g) => <button key={g.id} type="button" className={"chip" + (d.acordado.includes(g.id) ? " activo" : "")} onClick={() => u("acordado")(d.acordado.includes(g.id) ? d.acordado.filter((x) => x !== g.id) : [...d.acordado, g.id])}>{g.nombre}</button>)}
          </div>
        </Campo>
      </div>
    </Modal>
  );
}

function FormTerminos({ ctx, cerrar }: { ctx: Ctx; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const poner = usePonerTerminos(ctx.casoId, ctx.cuentaId);
  const [d, setD] = useState({ base: "", pct: 20, exclusiones: ["", "", "", "", ""], dependencias: [{ texto: "", quien: "" }], sinDeps: false });
  const [error, setError] = useState("");
  const cliente = ctx.personas.filter((p) => p.organizacion === "cliente");
  const guardar = async () => {
    const exclusiones = d.exclusiones.map((x) => x.trim()).filter(Boolean);
    const deps = d.dependencias.filter((x) => x.texto.trim());
    if (!d.base || exclusiones.length < 5) { setError("Hacen falta la base fija y al menos cinco exclusiones de atribución."); return; }
    if (!d.sinDeps && deps.length === 0) { setError("Registra las dependencias del cliente o declara que no hay."); return; }
    try {
      await poner.mutateAsync({
        base_fija: Number(d.base), fee_at_risk_pct: d.pct, exclusiones_atribucion: exclusiones,
        sin_dependencias_declarado: d.sinDeps,
        dependencias: deps.map((x, i) => ({ id: `dep-${i + 1}`, descripcion: x.texto.trim(), responsable_id: x.quien || cliente[0]?.id || "", sla_dias: 5, suspende_garantia: true })),
      });
      cerrar(); avisar("Términos D5 capturados");
    } catch (e) { setError(mensaje(e)); }
  };
  return (
    <Modal titulo="Términos · D5" sub="Base fija, fee-at-risk, dependencias del cliente y exclusiones de atribución." onCerrar={cerrar} ancho={620}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={poner.isPending} onClick={guardar}>Guardar términos</button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={grid2}>
          <Campo label="Base fija" pista="MXN" error={error}><input autoFocus type="number" step="1000" value={d.base} onChange={(e) => setD((x) => ({ ...x, base: e.target.value }))} /></Campo>
          <Campo label="Fee-at-risk %"><input type="number" min={0} max={50} value={d.pct} onChange={(e) => setD((x) => ({ ...x, pct: Number(e.target.value) }))} /></Campo>
        </div>
        <Campo label="Exclusiones de atribución" pista="Cinco como mínimo: lo que no es atribuible al encargo">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {d.exclusiones.map((x, i) => <input key={i} value={x} onChange={(e) => setD((s) => ({ ...s, exclusiones: s.exclusiones.map((y, j) => (j === i ? e.target.value : y)) }))} placeholder={`Exclusión ${i + 1}`} />)}
            <button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5, alignSelf: "flex-start" }} onClick={() => setD((s) => ({ ...s, exclusiones: [...s.exclusiones, ""] }))}>+ Otra</button>
          </div>
        </Campo>
        <Campo label="Dependencias del cliente" pista="Lo que suspende la garantía si no llega a tiempo">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {d.dependencias.map((x, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 6 }}>
                <input value={x.texto} onChange={(e) => setD((s) => ({ ...s, dependencias: s.dependencias.map((y, j) => (j === i ? { ...y, texto: e.target.value } : y)) }))} placeholder="Acceso de solo lectura al ERP" />
                <select value={x.quien} onChange={(e) => setD((s) => ({ ...s, dependencias: s.dependencias.map((y, j) => (j === i ? { ...y, quien: e.target.value } : y)) }))}>
                  <option value="">Responsable…</option>{cliente.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </div>
            ))}
            <span style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5 }} onClick={() => setD((s) => ({ ...s, dependencias: [...s.dependencias, { texto: "", quien: "" }] }))}>+ Otra</button>
              <label style={{ fontSize: 12.5, display: "flex", gap: 6, alignItems: "center" }}><input type="checkbox" checked={d.sinDeps} onChange={(e) => setD((s) => ({ ...s, sinDeps: e.target.checked }))} style={{ width: "auto" }} /> No hay dependencias del cliente</label>
            </span>
          </div>
        </Campo>
      </div>
    </Modal>
  );
}

// ─── Gobernanza y artefacto, que viven junto a las sesiones ─────────────────

export function ConfirmarGobernanza({ ctx, persona, cerrar }: { ctx: Ctx; persona: Persona; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const confirmar = useConfirmarGobernanza(ctx.casoId, ctx.cuentaId);
  const [hid, setHid] = useState("");
  const [error, setError] = useState("");
  return (
    <Modal titulo="Confirmar gobernanza" sub={`${persona.nombre} · quién firma y quién veta es una afirmación hasta que un hallazgo la respalda`} onCerrar={cerrar} ancho={560}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={!hid || confirmar.isPending}
        onClick={async () => { try { await confirmar.mutateAsync({ personaId: persona.id, hallazgo_id: hid }); cerrar(); avisar(`Gobernanza de ${persona.nombre} confirmada`); } catch (e) { setError(mensaje(e)); } }}>Confirmar</button></>}>
      <Campo label="Hallazgo que lo respalda" error={error}>
        <PickHallazgos hallazgos={ctx.verificados} elegidos={hid ? [hid] : []} onChange={(v) => setHid(v[v.length - 1] ?? "")} vacio="Sin hallazgos verificados no se puede confirmar." />
      </Campo>
    </Modal>
  );
}

export function BotonGobernanza({ ctx, persona }: { ctx: Ctx; persona: Persona }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      <button className="btn btn-fantasma" aria-label={`Confirmar gobernanza de ${persona.nombre}`} style={{ padding: 0, fontSize: 11.5 }} onClick={() => setAbierto(true)}>confirmar</button>
      {abierto && <ConfirmarGobernanza ctx={ctx} persona={persona} cerrar={() => setAbierto(false)} />}
    </>
  );
}

export function BotonArtefacto({ ctx, auto, onCerrar }: { ctx: Ctx; auto?: boolean; onCerrar?: () => void }) {
  const { avisar } = useAvisos();
  const poner = usePonerArtefacto(ctx.casoId, ctx.cuentaId);
  const [abierto, setAbierto] = useState(!!auto);
  // Se proponen las citas y dolores verificados, y siempre quedan renglones vacíos:
  // la hoja son cinco a siete viñetas y casi nunca salen todas del grafo.
  const citas = ctx.verificados.filter((h) => h.tipo === "quote" || h.tipo === "dolor").slice(0, 7).map((h) => h.contenido);
  const conHuecos = (base: string[]) => [...base, "", "", "", "", ""].slice(0, Math.max(5, base.length + 1));
  const [vinetas, setVinetas] = useState<string[]>(conHuecos(citas));
  const [error, setError] = useState("");
  const ya = ctx.vista.cierre.la_piedra !== null;
  const cerrar = () => { setAbierto(false); onCerrar?.(); };
  return (
    <>
      {!auto && (
        <button className="btn btn-secundario" onClick={() => { setVinetas(conHuecos(citas)); setAbierto(true); }}>
          {ya ? "Ver «Lo que escuchamos»" : "Generar «Lo que escuchamos»"}
        </button>
      )}
      {abierto && (
        <Modal titulo="Lo que escuchamos" sub="Cinco a siete viñetas, en las palabras del cliente. Es lo que se le entrega al cerrar La Plática." onCerrar={cerrar} ancho={600}
          pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={poner.isPending}
            onClick={async () => {
              const limpias = vinetas.map((v) => v.trim()).filter(Boolean);
              if (limpias.length < 3) { setError("Al menos tres viñetas: con menos, la hoja no le dice nada al cliente."); return; }
              try { await poner.mutateAsync({ id: idNuevo("art"), tipo: "lo_que_escuchamos", contenido: { vinetas: limpias } }); cerrar(); avisar("Hoja «Lo que escuchamos» entregada"); }
              catch (e) { setError(mensaje(e)); }
            }}>Entregar</button></>}>
          <Campo label="Viñetas" error={error} pista="Salen de las citas y los dolores verificados; se pueden editar">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {vinetas.map((v, i) => <input key={i} value={v} onChange={(e) => setVinetas((s) => s.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Viñeta ${i + 1}`} />)}
              <button className="btn btn-fantasma" style={{ padding: 0, fontSize: 12.5, alignSelf: "flex-start" }} onClick={() => setVinetas((s) => [...s, ""])}>+ Otra</button>
            </div>
          </Campo>
        </Modal>
      )}
    </>
  );
}

export type { Ctx };

/**
 * La hoja de un solo número: el entregable del cierre y lo único que recibe el
 * cliente. Registrarla es lo que cierra D5; el documento se abre en el registro
 * de marca, aparte del workbench.
 */
function HojaDeUnNumero({ ctx, listo, auto, onCerrar }: { ctx: Ctx; listo: boolean; auto?: boolean; onCerrar?: () => void }) {
  const { avisar } = useAvisos();
  const poner = usePonerArtefacto(ctx.casoId, ctx.cuentaId);
  const ya = ctx.vista.cierre.la_piedra !== null;
  const [error, setError] = useState("");
  const generar = async () => {
    const c = ctx.vista.cierre;
    try {
      await poner.mutateAsync({
        id: idNuevo("hoja"), tipo: "hoja_de_un_solo_numero",
        contenido: {
          encargo: c.oportunidad?.titulo ?? "",
          numero: c.baseline ? `${c.baseline.numero} ${c.baseline.unidad}` : "",
          indicador: c.indicador?.kpi ?? "",
        },
      });
      avisar("Hoja de un solo número generada");
      onCerrar?.();
    } catch (e) { setError(mensaje(e)); }
  };
  // Llamada desde un atajo: se genera y se cierra, sin pedir nada más.
  if (auto) {
    return (
      <Modal titulo="Generar la hoja de un solo número" sub="Es el entregable del cierre y lo único que recibe el cliente." onCerrar={() => onCerrar?.()} ancho={460}
        pie={<><button className="btn btn-secundario" onClick={() => onCerrar?.()}>Cancelar</button><button className="btn btn-navy" disabled={poner.isPending} onClick={generar}>Generar</button></>}>
        <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>Se arma con el encargo, el baseline y el indicador ya acordados.{error && <span style={{ display: "block", color: "var(--risk)", marginTop: 8 }}>{error}</span>}</p>
      </Modal>
    );
  }
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <button className="btn btn-navy" disabled={!listo || poner.isPending} onClick={generar}>{ya ? "Regenerar la hoja" : "Generar la hoja de un solo número"}</button>
      <a href={`/legacy/hoja.html?caso=${encodeURIComponent(ctx.casoId)}`} target="_blank" rel="noreferrer" className="btn btn-secundario" style={{ textDecoration: "none" }}>Verla ↗</a>
      {!listo && <span style={{ fontSize: 11.5, color: "var(--faint)" }}>falta cerrar baseline, indicador y términos</span>}
      {error && <span style={{ fontSize: 11.5, color: "var(--risk)" }}>{error}</span>}
    </div>
  );
}

/** Atajo de gobernanza: elegir a quién confirmar cuando el requisito lo pide. */
function ElegirGobernanza({ ctx, cerrar }: { ctx: Ctx; cerrar: () => void }) {
  const pendientes = ctx.personas.filter((p) => (p.es_economic_buyer || p.puede_vetar) && !p.gobernanza_confirmada);
  const [quien, setQuien] = useState(pendientes[0]?.id ?? "");
  const persona = pendientes.find((p) => p.id === quien);
  if (persona) return <ConfirmarGobernanza ctx={ctx} persona={persona} cerrar={cerrar} />;
  return (
    <Modal titulo="Confirmar gobernanza" onCerrar={cerrar} ancho={460}
      pie={<button className="btn btn-secundario" onClick={cerrar}>Cerrar</button>}>
      <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>
        No hay nadie marcado como quien firma o quien puede vetar. Agrégalo en «Personas del cliente» con esa marca y vuelve aquí.
      </p>
      <input type="hidden" value={quien} onChange={(e) => setQuien(e.target.value)} />
    </Modal>
  );
}

function AvisoCongelar({ cerrar }: { cerrar: () => void }) {
  return (
    <Modal titulo="Congelar el ranking" sub="El botón está en la tarjeta de la cartera, debajo de las fichas." onCerrar={cerrar} ancho={440}
      pie={<button className="btn btn-primario" onClick={cerrar}>Entendido</button>}>
      <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>Solo se puede congelar cuando las tres fichas tienen consenso acordado: el ranking que se congela es el que se le enseña al cliente.</p>
    </Modal>
  );
}
